import { useCreatePracticeMutation, useVerifyTenantMutation } from "api";
import Site from "api/types/models/site";
import {
  BackButton,
  Button,
  IconButton,
  FormGroup,
  Input,
  Label,
  Toggle,
  MinusIcon,
  PlusIcon,
  Modal,
  NotifierNotification,
} from "@mwi/ui";
import useErrorHandler from "hooks/useErrorHandler";
import { useEffect, useState } from "react";
import { useNotifier } from "react-headless-notifier";
import { useFieldArray, useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";

type Inputs = {
  merlin_id: string;
  subdomain: string;
  display_name: string;
  email: string;
  phone: string;
  address: string;
  website_link: string;
  privacy_link: string;
  emails: {
    value: string;
  }[];
};

const NewPracticeModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { notify } = useNotifier();
  const errorHandler = useErrorHandler();

  const [step, setStep] = useState(1);
  const [locations, setLocations] = useState<Site[]>([]);
  const [enabledLocations, setEnabledLocations] = useState<string[]>([]);

  const {
    handleSubmit,
    register,
    setError,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      emails: [{ value: "" }],
    },
  });

  const { fields, insert, remove } = useFieldArray({
    control,
    name: "emails",
  });

  const [createPractice, { data: createdPractice, isLoading, error }] =
    useCreatePracticeMutation();
  const [verifyTenant, { data: tenantData, error: verifyError }] =
    useVerifyTenantMutation();

  const [debouncedTenant] = useDebounce(watch("merlin_id"), 400);

  useEffect(() => {
    if (debouncedTenant) {
      verifyTenant({ merlin_id: debouncedTenant });
    }
  }, [debouncedTenant]);

  useEffect(() => {
    if (error || verifyError) {
      errorHandler(error ?? verifyError, setError);
    }
  }, [error, verifyError]);

  useEffect(() => {
    if (createdPractice) {
      if (step === 1) {
        setStep(2);
        setLocations(createdPractice.locations!);
      } else {
        onClose();
        notify(
          <NotifierNotification
            title="Success!"
            message="New practice has been added to the platform"
          />,
        );
      }
    }
  }, [createdPractice]);

  const afterLeave = () => {
    reset();
    setStep(1);
  };

  const triggerSubmit = (data: Inputs) => {
    createPractice({
      ...data,
      step,
      emails: data.emails.filter((e) => e.value),
      locations: enabledLocations,
    });
  };

  const toggleLocation = (id: string) => {
    enabledLocations.includes(id)
      ? setEnabledLocations(enabledLocations.filter((l) => l !== id))
      : setEnabledLocations([...enabledLocations, id]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose()}
      afterLeave={afterLeave}
      className="tablet:!max-w-[775px]"
    >
      <form onSubmit={handleSubmit(triggerSubmit)}>
        {step === 1 ? (
          <>
            <Modal.Header title="Add New Vet Practice" />

            <h3 className="mb-8 text-lg font-semibold">Practice Details</h3>

            <FormGroup
              error={errors?.merlin_id?.message}
              success={
                tenantData?.name
                  ? `Valid practice: ${tenantData.name}`
                  : undefined
              }
            >
              <Label>Tenant ID</Label>
              <Input {...register("merlin_id")} />
            </FormGroup>

            <FormGroup error={errors?.subdomain?.message}>
              <Label>Subdomain</Label>
              <Input {...register("subdomain")} />
            </FormGroup>

            <FormGroup error={errors?.display_name?.message}>
              <Label>Practice Name</Label>
              <Input {...register("display_name")} />
            </FormGroup>

            <FormGroup error={errors?.address?.message}>
              <Label>Practice Address</Label>
              <Input {...register("address")} />
            </FormGroup>

            <FormGroup error={errors?.phone?.message}>
              <Label>Practice Phone</Label>
              <Input type="tel" maxChars={14} {...register("phone")} />
            </FormGroup>

            <FormGroup error={errors?.email?.message}>
              <Label>Practice Email</Label>
              <Input {...register("email")} />
            </FormGroup>

            <FormGroup error={errors?.website_link?.message}>
              <Label>Practice Website Link</Label>
              <Input {...register("website_link")} />
            </FormGroup>

            <FormGroup error={errors?.privacy_link?.message}>
              <Label>Practice Privacy Policy Link</Label>
              <Input {...register("privacy_link")} />
            </FormGroup>

            <h3 className="mt-2 mb-8 text-lg font-semibold">Add Users</h3>

            <Label>Email Addresses</Label>
            {fields.map((field, index) => (
              <div className="flex" key={field.id}>
                <FormGroup
                  error={errors?.emails?.[index]?.value?.message}
                  className="flex-grow"
                >
                  <Input {...register(`emails.${index}.value`)} />
                </FormGroup>

                <div className="flex items-center pb-6 ml-12 gap-9">
                  <IconButton onClick={() => insert(index + 1, { value: "" })}>
                    <PlusIcon className="w-4 h-4" />
                  </IconButton>
                  <IconButton
                    onClick={() => remove(index)}
                    disabled={index === 0}
                  >
                    <MinusIcon className="w-4 h-4" />
                  </IconButton>
                </div>
              </div>
            ))}

            <Button type="submit" loading={isLoading} className="mt-10">
              Next
            </Button>
          </>
        ) : (
          <>
            <BackButton onClick={() => setStep(1)} className="mb-10" />

            <Modal.Header
              title="Add New Vet Practice"
              className="tablet:!pb-6"
            />

            <p className="mb-10">Configure your locations</p>

            <div className="flex flex-col gap-6">
              {locations?.map((location) => (
                <div className="flex" key={location.id}>
                  <p className="flex-grow">{location.name}</p>
                  <Toggle onChange={() => toggleLocation(location.id)} />
                </div>
              ))}
            </div>

            <Button type="submit" loading={isLoading} className="mt-10">
              Save
            </Button>
          </>
        )}
      </form>
    </Modal>
  );
};

export default NewPracticeModal;
